import { GUIDE_ROUTES } from "./guideRoutes/structure.js";
import { LANDING_PAGE_ROUTES } from "./landingPages/structure.js";
import { SUPPORTED_LOCALES, normalizeLocale } from "./translate.js";

/** @typedef {"nl" | "en"} SeoLocale */

const HOME_PATHS = {
  nl: "/",
  en: "/en",
};

/**
 * @param {string} pathname
 * @returns {string | null}
 */
function resolveBasePath(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";

  if (normalized === "/" || normalized === "/en") {
    return "/";
  }

  const landing = LANDING_PAGE_ROUTES.find(
    (route) => route.paths.nl === normalized || route.paths.en === normalized,
  );
  if (landing) {
    return landing.paths.nl;
  }

  const guide = GUIDE_ROUTES.find(
    (route) => route.paths.nl === normalized || route.paths.en === normalized,
  );
  if (guide) {
    return guide.paths.nl;
  }

  return null;
}

/**
 * @param {string} basePath
 * @param {string | undefined} locale
 * @returns {string | null}
 */
function pathForBasePath(basePath, locale) {
  const normalizedBasePath = basePath.replace(/\/+$/, "") || "/";
  const resolvedLocale = normalizeLocale(locale);

  if (normalizedBasePath === "/") {
    return HOME_PATHS[resolvedLocale];
  }

  const landing = LANDING_PAGE_ROUTES.find((route) => route.paths.nl === normalizedBasePath);
  if (landing) {
    return landing.paths[resolvedLocale];
  }

  const guide = GUIDE_ROUTES.find((route) => route.paths.nl === normalizedBasePath);
  if (guide) {
    return guide.paths[resolvedLocale];
  }

  return null;
}

/**
 * @param {string | undefined} locale
 * @returns {string}
 */
export function getLocalePrefix(locale) {
  return normalizeLocale(locale) === "en" ? "/en" : "";
}

/**
 * Localize a public route using its Dutch base path (`/`, `/zaagplan-mdf`, `/handleiding/...`).
 *
 * @param {string} path
 * @param {string | undefined} locale
 * @returns {string}
 */
export function localizePath(path, locale) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const basePath = resolveBasePath(normalizedPath) ?? normalizedPath;
  const localized = pathForBasePath(basePath, locale);

  if (localized) {
    return localized;
  }

  const prefix = getLocalePrefix(locale);
  if (basePath === "/") {
    return prefix || "/";
  }

  return `${prefix}${basePath}`;
}

/**
 * @param {string} pathname
 * @returns {{ locale: SeoLocale, path: string }}
 */
export function parseLocalizedPathname(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const basePath = resolveBasePath(normalized);

  if (basePath) {
    const locale = normalized === basePath || (basePath === "/" && normalized === "/")
      ? "nl"
      : "en";
    return { locale, path: basePath };
  }

  if (normalized === "/en" || normalized.startsWith("/en/")) {
    return { locale: "en", path: "/" };
  }

  return { locale: "nl", path: normalized };
}

/** @returns {string[]} */
export function getSeoBasePaths() {
  return [
    "/",
    ...LANDING_PAGE_ROUTES.map((page) => page.paths.nl),
    ...GUIDE_ROUTES.map((guide) => guide.paths.nl),
  ];
}

/**
 * @returns {{ locale: SeoLocale, path: string, basePath: string }[]}
 */
export function buildPrerenderEntries() {
  return getSeoBasePaths().flatMap((basePath) => (
    SUPPORTED_LOCALES.map((locale) => ({
      locale: /** @type {SeoLocale} */ (normalizeLocale(locale)),
      basePath,
      path: localizePath(basePath, locale),
    }))
  ));
}

/**
 * @param {string} basePath
 * @returns {{ locale: SeoLocale, href: string }[]}
 */
export function buildHreflangAlternates(basePath, siteUrl) {
  const origin = siteUrl.replace(/\/$/, "");

  return SUPPORTED_LOCALES.map((locale) => ({
    locale: /** @type {SeoLocale} */ (normalizeLocale(locale)),
    href: `${origin}${localizePath(basePath, locale)}`,
  }));
}
